import { useEffect, useMemo, useState } from "react";
import type { NormRect, PdfSelection } from "./types";
import { clientRectsToNormRects } from "../utils/rects";

export function usePdfSelection() {
  const [selection, setSelection] = useState<PdfSelection | null>(null);

  useEffect(() => {
    const onMouseUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;

      const text = sel.toString().trim();
      if (!text) {
        setSelection(null);
        return;
      }

      const range = sel.getRangeAt(0);
      const clientRects = Array.from(range.getClientRects());
      if (!clientRects.length) {
        setSelection(null);
        return;
      }

      // Group rects by page - find the canvas wrapper (pageInnerRef) not the page container
      const pageRectsMap = new Map<number, { canvasWrapper: HTMLElement; rects: DOMRect[] }>();

      for (const rect of clientRects) {
        if (rect.width === 0 || rect.height === 0) continue;

        const midX = rect.left + rect.width / 2;
        const midY = rect.top + rect.height / 2;

        const el = document.elementFromPoint(midX, midY);
        const pageEl = el?.closest("[data-page-number]") as HTMLElement | null;
        if (!pageEl) continue;

        const pageNumber = Number(pageEl.getAttribute("data-page-number"));
        if (!pageNumber) continue;

        // Find the canvas wrapper inside the page container (the relative div that contains the canvas)
        // This is the actual area where annotations are rendered
        const canvasWrapper = pageEl.querySelector(".relative") as HTMLElement | null;
        if (!canvasWrapper) continue;

        if (!pageRectsMap.has(pageNumber)) {
          pageRectsMap.set(pageNumber, { canvasWrapper, rects: [] });
        }
        pageRectsMap.get(pageNumber)!.rects.push(
          new DOMRect(rect.left, rect.top, rect.width, rect.height)
        );
      }

      if (pageRectsMap.size === 0) {
        setSelection(null);
        return;
      }

      // For single-page selection (most common case), use that page
      // For multi-page, use the first page with rects
      const sortedPages = Array.from(pageRectsMap.keys()).sort((a, b) => a - b);
      const primaryPage = sortedPages[0];
      const { canvasWrapper, rects } = pageRectsMap.get(primaryPage)!;

      // Normalize rects against the canvas wrapper (not the page container with padding)
      const canvasRect = canvasWrapper.getBoundingClientRect();
      const rectsNorm = clientRectsToNormRects(rects, canvasRect);

      // Anchor = first visible rect
      const anchorClientRect =
        rects.find((r) => r.width > 0 && r.height > 0) ?? canvasRect;

      setSelection({
        pageNumber: primaryPage,
        text,
        rects: rectsNorm,
        anchorClientRect,
      });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        window.getSelection()?.removeAllRanges();
        setSelection(null);
      }
    };

    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const api = useMemo(
    () => ({
      selection,
      clearSelection: () => {
        window.getSelection()?.removeAllRanges();
        setSelection(null);
      },
      setSelection,
    }),
    [selection]
  );

  return api;
}
